// filepath: /Users/shinc/IdeaProjects/manuals/manuals-front/src/components/common/BulkProgressModal.js
import React, {useMemo, useState} from 'react';
import {Alert, Button, Modal, Progress, Radio, Space, Table, Tag, Typography} from 'antd';

const { Text } = Typography;

function toCsv(rows) {
  if (!rows?.length) return 'key,label,reason\n';
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const header = ['key','label','reason'];
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push([escape(r.key), escape(r.label), escape(r.reason)].join(','));
  }
  return lines.join('\n');
}

const BulkProgressModal = ({
  open,
  title = '일괄 처리',
  total = 0,
  current = 0,
  success = 0,
  fail = 0,
  failures = [],
  entries = [], // [{ key,label,status:'success'|'fail',reason? }]
  running = false,
  onCancel,
  onStop,
  onRetryOne,
  onRetryAll,
}) => {
  const percent = total > 0 ? Math.round((current / total) * 100) : 0;
  const done = !running && total > 0 && current >= total;

  // 백워드 호환: entries 가 없으면 failures를 entries로 변환
  const normalizedEntries = useMemo(() => {
    if (entries && entries.length) return entries;
    return (failures || []).map(f => ({ ...f, status: 'fail' }));
  }, [entries, failures]);

  const [filter, setFilter] = useState('all'); // all | success | fail
  const filteredEntries = useMemo(() => {
    if (filter === 'all') return normalizedEntries;
    return (normalizedEntries || []).filter(e => e.status === filter);
  }, [normalizedEntries, filter]);

  const failedOnly = useMemo(() => (normalizedEntries || []).filter(e => e.status === 'fail'), [normalizedEntries]);

  const downloadCsv = () => {
    try {
      const csv = toCsv((failedOnly || []));
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}_failures.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('다운로드에 실패했습니다.');
    }
  };

  const columns = [
    { title: '#', dataIndex: 'index', width: 60, render: (_, __, idx) => idx + 1 },
    { title: '대상', dataIndex: 'label' },
    { title: '상태', dataIndex: 'status', width: 100, render: (v) => v === 'success' ? <Tag color="green">성공</Tag> : <Tag color="red">실패</Tag> },
    { title: '사유', dataIndex: 'reason' },
    ...(onRetryOne && !running ? [{
      title: '액션', key: 'actions', width: 120,
      render: (_, row) => row.status === 'fail' ? (<Button size="small" onClick={() => onRetryOne(row)}>재시도</Button>) : null
    }] : [])
  ];

  return (
    <Modal
      open={open}
      title={title}
      onCancel={running ? undefined : onCancel}
      footer={
        <Space>
          {running && (
            <Button danger onClick={onStop}>중지</Button>
          )}
          {!running && failedOnly.length > 0 && onRetryAll && (
            <Button onClick={onRetryAll}>실패 전체 재시도</Button>
          )}
          {failedOnly.length > 0 && (
            <Button onClick={downloadCsv}>실패내역 다운로드</Button>
          )}
          <Button type="primary" onClick={onCancel} disabled={running}>
            {done ? '닫기' : '취소'}
          </Button>
        </Space>
      }
      maskClosable={false}
      closable={!running}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <div>
          <Text strong>진행률</Text>
          <Progress percent={percent} status={running ? 'active' : (fail > 0 ? 'exception' : 'normal')} />
          <div style={{ color: '#666' }}>전체 {total}건 / 진행 {current}건, 성공 {success}건, 실패 {fail}건</div>
        </div>

        {/* 성공/실패 필터 */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <Radio.Group value={filter} onChange={(e)=> setFilter(e.target.value)}>
            <Radio.Button value="all">전체</Radio.Button>
            <Radio.Button value="success">성공</Radio.Button>
            <Radio.Button value="fail">실패</Radio.Button>
          </Radio.Group>
          {failedOnly.length > 0 && (
            <Alert type="warning" showIcon message={`실패 ${failedOnly.length}건`} style={{ marginLeft: 8 }} />
          )}
        </div>

        {(filteredEntries?.length > 0) && (
          <Table
            size="small"
            rowKey={(r, i) => r.key ?? i}
            columns={columns}
            dataSource={filteredEntries}
            pagination={{ pageSize: 5 }}
          />
        )}
      </Space>
    </Modal>
  );
};

export default BulkProgressModal;
